/* ----------------- Constants --------------- */
import { events, type SessionSource } from "@constants/tracking";
import type { ExamStatus } from "@memorang/types";

/* ----------------- React & Hooks --------------- */
import { useState } from "react";

/* ----------------- Features --------------- */
import useSessionStore from "@features/session/hooks/useSessionStore";

/* ----------------- Utils --------------- */
import { useRouter } from "expo-router";

import { usePurchaseDialog } from "@contexts/DialogContext/dialogs/purchase";
import { IS_MOCKED_SESSION } from "@features/session/constants";
import { flattenSessionItems } from "@features/session/helpers/session";
import { useResumeSession } from "@features/session/hooks/useResumeSession";
import { useSession } from "@features/session/hooks/useSession";
import { getIsOlxExam } from "@helpers/exams";
import { useCanBypassPaywall } from "@hooks/useCanBypassPaywall";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { useSingleFlightNavigation } from "@hooks/useSingleFlightNavigation";
import type { BaseItemSchema, SessionResponse } from "@memorang/types";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useToast } from "@memorang/ui/components/Toast";
/* ----------------- Analytics --------------- */
import { trackCustomEvent } from "analytics";
export const usePracticeTests = () => {
	const [selectedExamId, setSelectedExamId] = useState<string | undefined>();

	const [showResumeSessionInfoDialog, setShowResumeSessionInfoDialog] =
		useState(false);
	const [selectedNumericId, setSelectedNumericId] = useState<
		number | undefined
	>();
	const [creatingSession, setCreatingSession] = useState(false);
	const [showErrorDialog, setShowErrorDialog] = useState(false);

	const { showToast } = useToast();

	const {
		currentExamContext: { projectId, organizationId },
	} = useExamContext();

	const [showCollectUserDetailsDialog, setShowCollectUserDetailsDialog] =
		useState(false);

	const [olxError, setOlxError] = useState<{
		title: string;
		message: string;
	} | null>(null);

	const [olxExamEligibility, setOlxExamEligibility] = useState<{
		endDate: string;
		startDate: string;
	} | null>(null);

	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const updateSession = useSessionStore((state) => state.updateSession);

	const sessionNumericId = useSessionStore((state) => state.numericId);

	const toggleShowCollectUserDetailsDialog = () => {
		setShowCollectUserDetailsDialog((prev) => !prev);

		router.push({
			pathname: "/(protected)/session/[id]",
			params: {
				id: sessionNumericId,
				index: "1",
			},
		});
	};

	const { handleResumeSession } = useResumeSession();

	const { showPurchase } = usePurchaseDialog();
	const canBypassPaywall = useCanBypassPaywall();

	const router = useRouter();

	const navigateOnce = useSingleFlightNavigation();
	const {
		data: practiceTestDistribution,
		isLoading,
		isRefetching,
	} = useMixedItemDist({ action: "GET_PRATICETEST_DISTRIBUTION" });

	const { createNewSession } = useSession();

	const hasProductAccess = practiceTestDistribution?.hasAccess;

	const handleStartSession = async (
		examId: string,
		numericId?: number,
		isQuiz?: boolean,
		isOlx?: boolean,
		replace = true,
		collectionId?: number,
		source: SessionSource = "practice_tests",
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	) => {
		setSelectedExamId(examId);
		setCreatingSession(true);
		try {
			if (isQuiz) {
				trackCustomEvent({
					eventName: events.sessionDiagnosticClicked,
					numericId,
				});
			}

			let newSessionResponse: SessionResponse;

			if (IS_MOCKED_SESSION) {
				const { mockedDiagnosticSession } = await import(
					"@features/session/constants/mocked-diagnostic-session"
				);
				const { data } = mockedDiagnosticSession;
				const mockItems = data.sessionItems as unknown as BaseItemSchema[];
				const mockedFlattenedSessionItems = flattenSessionItems(
					mockItems,
					true,
				);
				newSessionResponse = {
					...data,
					sessionItems: mockedFlattenedSessionItems,
				} as unknown as SessionResponse;
			} else {
				const params = {
					collectionId: collectionId!,
					projectId,
					organizationId,
					sessionType: "SUMMATIVE" as const,
					sessionSubType: isQuiz ? "DIAGNOSTIC" : "PRACTICE_TEST",
				} as const;

				const result = await createNewSession(params);

				if (result.errors) {
					setOlxError({
						title: result.errors.title,
						message: result.errors.message,
					});
					return;
				}
				if (!result.sessionItems?.length) {
					showToast({
						message: "Failed to create session, try again",
					});
					return;
				}

				const flattenedSessionItems = flattenSessionItems(
					result.sessionItems,
					true,
				);

				newSessionResponse = {
					...result,
					sessionItems: flattenedSessionItems,
				};
			}

			trackCustomEvent({
				eventName: events.sessionStarted,
				sessionType: "SUMMATIVE",
				sessionSubType: isQuiz ? "DIAGNOSTIC" : "PRACTICE_TEST",
				source,
				numericId: newSessionResponse.numericId,
			});

			updateSession({
				sessionResponse: newSessionResponse,
				isSummativeTest: true,
				isDiagnostic: false,
			});
			if (isOlx) {
				setShowCollectUserDetailsDialog(true);
			} else {
				const routerFunction = replace ? router.replace : router.push;
				routerFunction({
					pathname: "/(protected)/session/[id]",
					params: {
						id: newSessionResponse.numericId,
						index: "1",
					},
				});
			}
		} catch (_error: unknown) {
			setShowErrorDialog(true);
		} finally {
			setSelectedExamId(undefined);
			setCreatingSession(false);
		}
	};
	const handleActiveSession = async (examId: string, numericId?: number) => {
		if (numericId) {
			setSelectedExamId(examId);
			setCreatingSession(true);
			await handleResumeSession(numericId);
			setSelectedExamId(undefined);
			setSelectedNumericId(undefined);
			setShowResumeSessionInfoDialog(false);
			setCreatingSession(false);
		}
	};
	const handlePracticeTestSession = async (
		examId: string,
		status: ExamStatus,
		numericId?: number,
		isQuiz?: boolean,
		hasExamAccess?: boolean,
		replace?: boolean,
		collectionId?: number,
		shouldStartSession?: boolean,
		isRecommendedTask?: boolean,
		//biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
	) => {
		resetSessionStore();
		switch (status) {
			case "COMPLETE":
			case "LEGACYREPORT":
			case "MIGRATEDREPORT":
				navigateOnce(() =>
					router.push({
						pathname: "/(protected)/session/[id]/report",
						params: {
							id: numericId as number,
							canGoBack: "true",
						},
					}),
				);
				break;
			case "ACTIVE":
				{
					if (isRecommendedTask || shouldStartSession) {
						await handleActiveSession(examId, numericId);
					} else {
						setSelectedNumericId(numericId);
						setShowResumeSessionInfoDialog(true);
					}
				}
				break;
			case "NOT_STARTED":
				if (
					(hasExamAccess && hasProductAccess) ||
					isQuiz ||
					shouldStartSession ||
					canBypassPaywall
				) {
					await handleStartSession(
						examId,
						numericId,
						isQuiz,
						getIsOlxExam(examId),
						replace,
						collectionId,
						isRecommendedTask ? "recommended_tasks" : "practice_tests",
					);
				} else {
					if (examId.includes("OLX")) {
						router.push("/redeem");
						return;
					}
					showPurchase({
						productType: "PRACTICE_TEST",
						fromPracticeTestPage: true,
					});
				}
				break;
			default:
				break;
		}
	};

	return {
		practiceTestDistribution,
		handlePracticeTestSession,
		selectedExamId,
		creatingSession,
		showErrorDialog,
		setShowErrorDialog,
		loading: isLoading || isRefetching,
		olxExamEligibility,
		setOlxExamEligibility,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		olxError,
		setOlxError,
		selectedNumericId,
		showResumeSessionInfoDialog,
		setShowResumeSessionInfoDialog,
		handleActiveSession,
	};
};
